import { useState, useMemo, useEffect } from 'react'
import { Search, Eye, X, Check, Ban, Clock, ShieldCheck, ArrowLeft } from 'lucide-react'
import { formatDate } from '../lib/format'
import DocumentApproval from '../components/DocumentApproval'
import { accessRequestsApi } from '../lib/api'
import { useToast } from '../lib/ToastContext'

const statusBadge = (status) => {
  const map = {
    Pending: 'border-amber-300 text-amber-700 bg-amber-50',
    Approved: 'border-emerald-300 text-emerald-700 bg-emerald-50',
    Denied: 'border-rose-300 text-rose-700 bg-rose-50',
  }
  return <span className={`inline-flex items-center px-2 py-0.5 text-[11px] font-semibold border ${map[status] || map.Pending}`}>{status}</span>
}

const accountBadge = (status) => {
  const map = {
    Active: 'border-emerald-300 text-emerald-700 bg-emerald-50',
    'Pending Verification': 'border-amber-300 text-amber-700 bg-amber-50',
    Suspended: 'border-rose-300 text-rose-700 bg-rose-50',
  }
  return <span className={`inline-flex items-center px-2 py-0.5 text-[11px] font-semibold border ${map[status] || 'border-neutral-300 text-neutral-600 bg-neutral-50'}`}>{status}</span>
}

export default function Approvals({ accessRequests: propRequests, onApproveAccess, onDenyAccess, companyId, onNavigate }) {
  const [requests, setRequests] = useState(propRequests || [])
  const [query, setQuery] = useState('')
  const [profileTarget, setProfileTarget] = useState(null)
  const [loading, setLoading] = useState(true)
  const { toast } = useToast()

  useEffect(() => {
    let mounted = true
    ;(async () => {
      if (!companyId) { setLoading(false); return }
      const { data, error } = await accessRequestsApi.list(companyId)
      if (!mounted) return
      if (!error && data) {
        setRequests(data.map((r) => ({
          id: r.id,
          investorName: r.investor_name,
          fund: r.investor_fund || 'Independent',
          email: r.investor_email,
          role: r.investor_role || 'Investor',
          stageFocus: r.stage_focus || [],
          checkSize: r.check_size || '—',
          date: (r.created_at || '').split('T')[0],
          status: r.status,
          accountStatus: r.account_status || 'Active',
        })))
      }
      setLoading(false)
    })()
    return () => { mounted = false }
  }, [companyId])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return requests
    return requests.filter((r) => [r.investorName, r.fund, r.email].join(' ').toLowerCase().includes(q))
  }, [query, requests])

  const handleApprove = async (id) => {
    setRequests((prev) => prev.map((r) => (r.id === id ? { ...r, status: 'Approved' } : r)))
    const { error } = await accessRequestsApi.approve(id)
    if (error) toast('Failed to approve: ' + error, 'error')
    else toast('Access request approved.', 'success')
    onApproveAccess?.(id)
  }

  const handleDeny = async (id) => {
    setRequests((prev) => prev.map((r) => (r.id === id ? { ...r, status: 'Denied' } : r)))
    const { error } = await accessRequestsApi.deny(id)
    if (error) toast('Failed to deny: ' + error, 'error')
    else toast('Access request denied.', 'info')
    onDenyAccess?.(id)
  }

  return (
    <div className="p-8 lg:p-10 space-y-8 animate-fade-in">
      <div>
        {/* {onNavigate && (
          <button onClick={() => onNavigate('dashboard')} className="btn-ghost text-xs flex items-center gap-1.5 mb-4">
            <ArrowLeft size={14} /> Back to Dashboard
          </button>
        )} */}
        <h1 className="text-2xl font-bold text-ink">Approvals</h1>
        <p className="text-sm text-muted mt-1">Review staff document uploads and investor access requests.</p>
      </div>

      {/* Staff Document Approvals */}
      <section>
        <h2 className="text-sm font-semibold uppercase tracking-[0.14em] text-neutral-500 mb-4">Staff Document Uploads</h2>
        <DocumentApproval />
      </section>

      {/* Investor Access Requests */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-semibold uppercase tracking-[0.14em] text-neutral-500">Investor Access Requests</h2>
        </div>

        <div className="surface">
          <div className="px-6 py-4 border-b border-neutral-200">
            <div className="relative max-w-md">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="input-flat pl-9"
                placeholder="Search requests by investor name, fund, or email..."
              />
            </div>
          </div>

          <div className="grid grid-cols-12 px-6 py-3 border-b border-neutral-200 text-[11px] font-semibold uppercase tracking-[0.1em] text-neutral-500">
            <div className="col-span-2">Investor</div>
            <div className="col-span-2">Fund</div>
            <div className="col-span-2">Stage Focus</div>
            <div className="col-span-2">Check Size</div>
            <div className="col-span-1">Date</div>
            <div className="col-span-1 text-center">Status</div>
            <div className="col-span-2 text-right">Actions</div>
          </div>

          <div className="divide-y divide-neutral-200">
            {loading && (
              <div className="px-6 py-10 text-center text-sm text-muted">Loading access requests...</div>
            )}
            {!loading && filtered.map((r) => (
              <div key={r.id} className="grid grid-cols-12 px-6 py-3 text-sm items-center">
                <div className="col-span-2">
                  <p className="text-ink">{r.investorName}</p>
                  <p className="text-xs text-neutral-500">{r.email}</p>
                </div>
                <div className="col-span-2 text-neutral-600">{r.fund}</div>
                <div className="col-span-2 flex flex-wrap gap-1">
                  {r.stageFocus.map((s) => (
                    <span key={s} className="px-1.5 py-0.5 text-[10px] font-semibold border border-neutral-200 bg-neutral-50 text-neutral-600">{s}</span>
                  ))}
                </div>
                <div className="col-span-2 font-mono text-xs text-neutral-600">{r.checkSize}</div>
                <div className="col-span-1 font-mono text-xs text-neutral-500">{formatDate(r.date)}</div>
                <div className="col-span-1 text-center">{statusBadge(r.status)}</div>
                <div className="col-span-2 flex justify-end gap-1.5">
                  <button onClick={() => setProfileTarget(r)} className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-semibold border border-neutral-300 text-neutral-600 hover:text-brand hover:border-brand transition-colors">
                    <Eye size={11} /> Profile
                  </button>
                  {r.status === 'Pending' && (
                    <>
                      <button onClick={() => handleApprove(r.id)} className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-semibold text-white bg-brand">
                        <Check size={11} />
                      </button>
                      <button onClick={() => handleDeny(r.id)} className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-semibold border border-neutral-300 text-neutral-600 hover:text-rose-600 hover:border-rose-300 transition-colors">
                        <Ban size={11} />
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
            {!loading && filtered.length === 0 && (
              <div className="px-6 py-10 text-center text-sm text-muted">No access requests match your search.</div>
            )}
          </div>
        </div>
      </section>

      {/* Investor Profile Modal */}
      {profileTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-ink/40" onClick={() => setProfileTarget(null)} aria-hidden />
          <div className="relative bg-surface border border-neutral-200 w-full max-w-md animate-fade-in">
            <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-200">
              <h2 className="text-sm font-semibold text-ink">Investor Profile</h2>
              <button onClick={() => setProfileTarget(null)} className="text-neutral-400 hover:text-ink"><X size={18} /></button>
            </div>
            <div className="px-6 py-5 space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-brand-soft flex items-center justify-center">
                  <ShieldCheck size={18} className="text-brand" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-ink">{profileTarget.investorName}</p>
                  <p className="text-xs text-muted">{profileTarget.role}</p>
                </div>
              </div>

              <div className="space-y-3 pt-2">
                <ProfileRow label="Fund Name" value={profileTarget.fund} />
                <ProfileRow label="Email" value={profileTarget.email} mono />
                <ProfileRow label="Role" value={profileTarget.role} />
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-neutral-500 mb-1.5">Stage Focus</p>
                  <div className="flex flex-wrap gap-1.5">
                    {profileTarget.stageFocus.map((s) => (
                      <span key={s} className="px-2 py-0.5 text-[11px] font-semibold border border-brand-soft bg-brand-soft text-brand">{s}</span>
                    ))}
                  </div>
                </div>
                <ProfileRow label="Check Size" value={profileTarget.checkSize} mono />
                <ProfileRow label="Request Date" value={formatDate(profileTarget.date)} mono />
                <div className="flex items-center justify-between pt-2 border-t border-neutral-200">
                  <span className="text-[11px] font-semibold uppercase tracking-[0.1em] text-neutral-500">Account Status</span>
                  {accountBadge(profileTarget.accountStatus)}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function ProfileRow({ label, value, mono }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-[11px] font-semibold uppercase tracking-[0.1em] text-neutral-500">{label}</span>
      <span className={`text-sm text-ink ${mono ? 'font-mono text-xs' : ''}`}>{value}</span>
    </div>
  )
}
